import React, { useEffect, useRef, useState } from 'react';
import { Trash2, Send, Pause, Mic } from 'lucide-react';
import { toast } from 'sonner';

const MAX_SECONDS = 60;
const BARS = 56;

// Onda en vivo: últimas muestras a la derecha, puntos a la izquierda mientras no hay datos; se adapta al ancho disponible
const Wave = ({ levels }) => {
  const ref = useRef(null); const [bars, setBars] = useState(BARS);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const ro = new ResizeObserver(([e]) => setBars(Math.max(8, Math.floor(e.contentRect.width / 6))));
    ro.observe(el); return () => ro.disconnect();
  }, []);
  const shown = levels.slice(-bars);
  const pad = Math.max(0, bars - shown.length);
  return (
    <div ref={ref} className="flex h-7 min-w-0 flex-1 items-center justify-end gap-[3px] overflow-hidden" data-testid="voice-wave">
      {Array.from({ length: pad }).map((_, i) => <span key={`d${i}`} className="h-[3px] w-[3px] shrink-0 rounded-full bg-slate-300" />)}
      {shown.map((v, i) => <span key={i} className="w-[3px] shrink-0 rounded-full bg-slate-700" style={{ height: `${Math.max(3, Math.round(v * 26))}px` }} />)}
    </div>
  );
};

// Nota de voz estilo WhatsApp: papelera · (● tiempo · onda) · pausa/reanudar · enviar. Máximo 1 minuto.
export const VoiceRecorder = ({ onSend, onCancel }) => {
  const [seconds, setSeconds] = useState(0);
  const [paused, setPaused] = useState(false);
  const [levels, setLevels] = useState([]);
  const recRef = useRef(null); const sendOnStop = useRef(false);
  const elapsed = useRef(0); const segStart = useRef(0); const raf = useRef(null); const lastSample = useRef(0);
  const cb = useRef({ onSend, onCancel }); cb.current = { onSend, onCancel };

  const recordedMs = () => elapsed.current + (recRef.current?.state === 'recording' ? Date.now() - segStart.current : 0);

  // Una sola captura por montaje: el micrófono se abre una vez y se cierra al desmontar (evita grabadoras duplicadas que mezclan audio)
  useEffect(() => {
    let stream, ctx, dead = false;
    const closeCtx = () => { if (ctx && ctx.state !== 'closed') ctx.close().catch(() => {}); };
    const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((t) => window.MediaRecorder?.isTypeSupported?.(t));
    navigator.mediaDevices?.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } }).then((s) => {
      if (dead) { s.getTracks().forEach((t) => t.stop()); return; }
      stream = s;
      const chunks = [];
      const rec = new MediaRecorder(s, mime ? { mimeType: mime, audioBitsPerSecond: 64000 } : undefined); recRef.current = rec;
      rec.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunks, { type: rec.mimeType || mime || 'audio/webm' });
        s.getTracks().forEach((t) => t.stop()); closeCtx();
        if (sendOnStop.current) cb.current.onSend({ url: URL.createObjectURL(blob), duration: Math.max(1, Math.min(MAX_SECONDS, Math.round(elapsed.current / 1000))), blob });
      };
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = ctx.createAnalyser(); analyser.fftSize = 512; ctx.createMediaStreamSource(s).connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      const tick = () => {
        raf.current = requestAnimationFrame(tick);
        if (rec.state !== 'recording') return;
        const now = performance.now();
        if (now - lastSample.current < 90) return;
        lastSample.current = now;
        const ms = recordedMs(); setSeconds(Math.min(MAX_SECONDS, Math.floor(ms / 1000)));
        if (ms >= MAX_SECONDS * 1000) { elapsed.current = MAX_SECONDS * 1000; rec.pause(); setPaused(true); toast.info('Límite de 1 minuto alcanzado'); return; }
        analyser.getByteTimeDomainData(buf);
        let sum = 0; for (let i = 0; i < buf.length; i++) { const d = (buf[i] - 128) / 128; sum += d * d; }
        setLevels((l) => [...l.slice(-BARS + 1), Math.min(1, Math.sqrt(sum / buf.length) * 3)]);
      };
      rec.start(); segStart.current = Date.now(); tick();
    }).catch(() => { if (!dead) { toast.error('No se pudo acceder al micrófono'); cb.current.onCancel(); } });
    return () => { dead = true; cancelAnimationFrame(raf.current); sendOnStop.current = false; if (recRef.current?.state !== 'inactive') recRef.current?.stop(); stream?.getTracks().forEach((t) => t.stop()); closeCtx(); };
  }, []);

  const togglePause = () => {
    const rec = recRef.current; if (!rec) return;
    if (rec.state === 'recording') { elapsed.current += Date.now() - segStart.current; rec.pause(); setPaused(true); }
    else if (rec.state === 'paused' && elapsed.current < MAX_SECONDS * 1000) { segStart.current = Date.now(); rec.resume(); setPaused(false); }
  };
  const send = () => {
    const rec = recRef.current; if (!rec || rec.state === 'inactive') return;
    if (rec.state === 'recording') elapsed.current += Date.now() - segStart.current;
    sendOnStop.current = true; rec.stop();
  };
  const atMax = elapsed.current >= MAX_SECONDS * 1000;

  return (
    <div className="flex h-11 min-w-0 flex-1 items-center gap-2" data-testid="voice-recorder">
      <button type="button" onClick={onCancel} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-rose-50 hover:text-rose-600" aria-label="Descartar" data-testid="voice-discard"><Trash2 className="h-5 w-5" /></button>
      <div className="flex h-11 min-w-0 flex-1 items-center gap-3 overflow-hidden rounded-lg border border-slate-200 bg-slate-100 px-3">
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full bg-rose-600 ${paused ? '' : 'animate-pulse'}`} />
        <span className="shrink-0 font-mono text-sm font-semibold tabular-nums text-slate-800" data-testid="voice-timer">{Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}</span>
        <Wave levels={levels} />
      </div>
      <button type="button" onClick={togglePause} disabled={atMax} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-700 transition hover:bg-slate-50 disabled:opacity-40" aria-label={paused ? 'Reanudar' : 'Pausar'} data-testid="voice-pause">{paused ? <Mic className="h-5 w-5 text-rose-600" /> : <Pause className="h-5 w-5" />}</button>
      <button type="button" onClick={send} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#0f1f4b] text-white transition hover:bg-[#1a2f66]" aria-label="Enviar nota de voz" data-testid="voice-send"><Send className="h-4 w-4" /></button>
    </div>
  );
};
